import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, RefreshCwIcon, InfoIcon, MailIcon, ExternalLinkIcon, CopyIcon, CheckIcon, GlobeIcon, UsersIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { emailNotificationService, EmailConfig } from '../../utils/emailNotifications';
export function EmailNotificationsConfig() {
  const [config, setConfig] = useState<EmailConfig>({
    publicKey: '',
    serviceId: '',
    templateId: '',
    enabled: true,
    senderName: 'Savidna'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  // Template variables reference
  const templateVars = [{
    name: 'to_email',
    desc: 'Correo del paciente'
  }, {
    name: 'to_name',
    desc: 'Nombre del paciente'
  }, {
    name: 'from_name',
    desc: 'Nombre del remitente (clínica)'
  }, {
    name: 'subject',
    desc: 'Asunto del correo'
  }, {
    name: 'message',
    desc: 'Mensaje completo formateado'
  }, {
    name: 'patient_name',
    desc: 'Nombre del paciente'
  }, {
    name: 'doctor_name',
    desc: 'Nombre del doctor'
  }, {
    name: 'service_name',
    desc: 'Nombre del servicio'
  }, {
    name: 'date',
    desc: 'Fecha de la cita'
  }, {
    name: 'time',
    desc: 'Hora de la cita'
  }, {
    name: 'duration',
    desc: 'Duración'
  }, {
    name: 'action_type',
    desc: 'Tipo de acción (Confirmada, Cancelada, etc.)'
  }];
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  useEffect(() => {
    const loadConfig = async () => {
      // First load from cache
      const savedConfig = emailNotificationService.getConfig();
      if (savedConfig) {
        setConfig({
          ...savedConfig,
          enabled: true
        });
      }
      // Then refresh from Supabase
      await emailNotificationService.loadFromSupabase();
      const freshConfig = emailNotificationService.getConfig();
      if (freshConfig) {
        setConfig({
          ...freshConfig,
          enabled: true
        });
      }
    };
    loadConfig();
  }, []);
  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    const result = await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (result.success && !result.error) {
      setSaveMessage('✅ Configuración guardada y compartida con todos los usuarios');
    } else if (result.success && result.error) {
      setSaveMessage(`⚠️ ${result.error}`);
    } else {
      setSaveMessage('❌ Error al guardar la configuración');
    }
    setIsSaving(false);
    setTimeout(() => setSaveMessage(''), 5000);
  };
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (!config.publicKey || !config.serviceId || !config.templateId) {
      setTestResult({
        success: false,
        message: 'Completa todos los campos: Public Key, Service ID y Template ID.'
      });
      setIsTesting(false);
      return;
    }
    setTestResult({
      success: true,
      message: 'Configuración válida. Los correos se enviarán automáticamente al gestionar citas para todos los usuarios.'
    });
    setIsTesting(false);
  };
  const handleCopyVar = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 1500);
  };
  const isConfigComplete = config.publicKey && config.serviceId && config.templateId;
  return <div className="space-y-6" data-id="element-2230">
      <div data-id="element-2231">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2232">
          <MailIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-2233" />
          Notificaciones por Correo (EmailJS)
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2234">
          Envía correos reales a tus pacientes directamente desde el navegador
        </p>
      </div>

      {/* Status Card - Always Active */}
      <Card className={`${isConfigComplete ? 'bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/30 dark:to-cyan-950/30 border-teal-200 dark:border-teal-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800'}`} data-id="element-2235">
        <div className="flex items-center justify-between" data-id="element-2236">
          <div className="flex items-center gap-4" data-id="element-2237">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${isConfigComplete ? 'bg-teal-500 dark:bg-teal-600 shadow-teal-500/20' : 'bg-amber-500 dark:bg-amber-600 shadow-amber-500/20'}`} data-id="element-2238">
              <MailIcon className="w-7 h-7 text-white" data-id="element-2239" />
            </div>
            <div data-id="element-2240">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2241">
                {isConfigComplete ? '✅ Notificaciones Activas' : '⚠️ Configuración Pendiente'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2242">
                {isConfigComplete ? 'Los correos se envían automáticamente al crear, cancelar o reprogramar citas' : 'Completa las credenciales de EmailJS para activar los correos automáticos'}
              </p>
            </div>
          </div>
          {/* Shared indicator instead of toggle */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 rounded-full border border-gray-200 dark:border-gray-700" data-id="element-2243">
            <UsersIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" data-id="element-2244" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400" data-id="element-2245">
              Compartido
            </span>
          </div>
        </div>
      </Card>

      {/* Global config notice */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm flex items-start gap-2" data-id="element-2246">
        <GlobeIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-2247" />
        <div data-id="element-2248">
          <p className="font-medium" data-id="element-2249">Configuración Global</p>
          <p className="text-blue-600 dark:text-blue-400 mt-0.5" data-id="element-2250">
            Esta configuración se comparte con todos los usuarios del sistema.
            Solo necesitas configurarlo una vez y funcionará para todos.
          </p>
        </div>
      </div>

      {saveMessage && <div className={`p-3 rounded-lg border text-sm ${saveMessage.startsWith('✅') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : saveMessage.startsWith('⚠️') ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-id="element-2251">
          {saveMessage}
        </div>}

      {/* EmailJS Configuration */}
      <Card data-id="element-2252">
        <div className="space-y-4" data-id="element-2253">
          <div className="flex items-center justify-between" data-id="element-2254">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-2255">
              Credenciales de EmailJS
            </h3>
            <a href="https://dashboard.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1" data-id="element-2256">
              Abrir Dashboard
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-2257" />
            </a>
          </div>

          <Input label="Public Key" value={config.publicKey} onChange={e => setConfig({
          ...config,
          publicKey: e.target.value
        })} placeholder="Ej: aBcDeFgHiJkLmN" helperText="Dashboard → Account → Public Key" data-id="element-2258" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2259">
            <Input label="Service ID" value={config.serviceId} onChange={e => setConfig({
            ...config,
            serviceId: e.target.value
          })} placeholder="Ej: service_abc123" helperText="Dashboard → Email Services → Service ID" data-id="element-2260" />
            <Input label="Template ID" value={config.templateId} onChange={e => setConfig({
            ...config,
            templateId: e.target.value
          })} placeholder="Ej: template_xyz789" helperText="Dashboard → Email Templates → Template ID" data-id="element-2261" />
          </div>

          <Input label="Nombre del Remitente" value={config.senderName} onChange={e => setConfig({
          ...config,
          senderName: e.target.value
        })} placeholder="Savidna - Centro de Bienestar" data-id="element-2262" />

          <div className="flex items-center gap-3 pt-4 flex-wrap" data-id="element-2263">
            <Button onClick={handleSave} isLoading={isSaving} data-id="element-2264">
              Guardar
            </Button>
            <Button variant="secondary" onClick={handleTestConnection} isLoading={isTesting} disabled={!isConfigComplete} data-id="element-2265">
              <RefreshCwIcon className="w-4 h-4" data-id="element-2266" />
              Verificar
            </Button>
            <Button variant="secondary" onClick={() => setShowInstructions(!showInstructions)} data-id="element-2267">
              <InfoIcon className="w-4 h-4" data-id="element-2268" />
              {showInstructions ? 'Ocultar' : 'Ver'} Instrucciones
            </Button>
          </div>

          {testResult && <div className={`p-3 rounded-lg border flex items-start gap-2 ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'}`} data-id="element-2269">
              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-2270" /> : <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-2271" />}
              <p className="font-medium whitespace-pre-line" data-id="element-2272">
                {testResult.message}
              </p>
            </div>}
        </div>
      </Card>

      {/* Instructions */}
      {showInstructions && <Card data-id="element-2273">
          <div className="space-y-6" data-id="element-2274">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-2275">
              Configuración en 4 Pasos
            </h3>

            <div className="space-y-6" data-id="element-2276">
              {/* Step 1 */}
              <div className="flex gap-4" data-id="element-2277">
                <div className="flex-shrink-0" data-id="element-2278">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-2279">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-2280">
                      1
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-2281">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2282">
                    Crea una cuenta en EmailJS
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2283">
                    Ve a{' '}
                    <a href="https://www.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline" data-id="element-2284">
                      emailjs.com
                    </a>{' '}
                    y regístrate gratis (200 correos/mes gratis).
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4" data-id="element-2285">
                <div className="flex-shrink-0" data-id="element-2286">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-2287">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-2288">
                      2
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-2289">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2290">
                    Conecta tu servicio de email
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2291">
                    Dashboard → Email Services → Add New Service → Conecta
                    Gmail, Outlook, etc. Copia el <strong data-id="element-2292">Service ID</strong>.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4" data-id="element-2293">
                <div className="flex-shrink-0" data-id="element-2294">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-2295">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-2296">
                      3
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-2297">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2298">
                    Crea una plantilla de email
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-2299">
                    Dashboard → Email Templates → Create New Template. Copia el{' '}
                    <strong data-id="element-2300">Template ID</strong>. Usa estas variables en tu
                    plantilla:
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-2301">
                    <div className="grid grid-cols-1 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2302">
                      {templateVars.map(v => <div key={v.name} className="flex items-center justify-between px-3 py-2" data-id="element-2303">
                          <div className="flex items-center gap-2" data-id="element-2304">
                            <code className="text-xs bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 px-1.5 py-0.5 rounded font-mono" data-id="element-2305">
                              {`{{${v.name}}}`}
                            </code>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2306">
                              {v.desc}
                            </span>
                          </div>
                          <button onClick={() => handleCopyVar(v.name)} className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 rounded transition-colors" title="Copiar" data-id="element-2307">
                            {copiedVar === v.name ? <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-2308" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-2309" />}
                          </button>
                        </div>)}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-2310">
                    💡 La variable más importante es{' '}
                    <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded" data-id="element-2311">
                      {'{{message}}'}
                    </code>{' '}
                    que contiene todo el mensaje formateado.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4" data-id="element-2312">
                <div className="flex-shrink-0" data-id="element-2313">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-2314">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-2315">
                      4
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-2316">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2317">
                    Copia tu Public Key
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2318">
                    Dashboard → Account → Public Key. Pégala arriba junto con el
                    Service ID y Template ID.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4" data-id="element-2319">
              <p className="text-sm text-teal-700 dark:text-teal-300" data-id="element-2320">
                <strong data-id="element-2321">✅ ¡Listo!</strong> Con EmailJS los correos se envían
                directamente desde el navegador. La configuración se comparte
                automáticamente con todos los usuarios.
              </p>
            </div>
          </div>
        </Card>}
    </div>;
}